<template>
    <cw-modal :title="formUser.name + ': ' + translate('edit_permissions')" :id="store.modals.USERS_EDIT_USER">
        <template v-slot>
            <form v-on:submit.prevent="save(formUser)" class="overflow-y-auto max-h-[70vh] p-2 -m-2">
                <div class="space-y-6">
                    <div class="grid auto-cols-fr grid-flow-col gap-6 tablet:flex tablet:flex-wrap">
                        <div>
                            <input :id="'userIsAdmin' + formUser.userId"
                                   type="checkbox"
                                   v-model="formUser.isAdmin"
                                   :disabled="!isLoggedInAsAdmin || cannotDemoteFromAdmin"/>
                            <label :for="'userIsAdmin' + formUser.userId"
                                   class="mb-0">{{ translate("beheerder") }}</label>
                        </div>
                        <div v-if="adminPermissions[Feature.VERVALDAGBOEK]">
                            <input type="checkbox" :id="'vervaldagboek' + formUser.userId"
                                   v-model="formUser.permissions.hasVervaldagboek"/>
                            <label
                                :for="'vervaldagboek' + formUser.userId">{{ translate('vervaldagboek') }}</label>
                        </div>
                    </div>
                    <div class="grid auto-cols-fr grid-flow-col gap-6 tablet:flex tablet:flex-wrap">
                        <div v-if="adminPermissions[Feature.ALERTS_PLUS]" class="w-full">
                            <label>Alerts+</label>
                            <select v-model="formUser.permissions.alertsPlusAccessLevel">
                                <option v-for="apAccesslevel in accountStore.apAccesslevels" :value="apAccesslevel"
                                        :key="'al'+apAccesslevel">
                                    {{ translate('alertsplus_accesslevel_' + apAccesslevel) }}
                                </option>
                            </select>
                        </div>
                        <div v-if="adminPermissions[Feature.INTERNATIONAL_REPORTS]" class="w-full">
                            <label>{{ translate('internationaal_rapport') }}</label>
                            <select v-model="formUser.permissions.internationalAccessLevel">
                                <option v-for="irAccesslevel in accountStore.irAccesslevels" :value="irAccesslevel"
                                        :key="'ir'+irAccesslevel">
                                    {{ translate('internationaal_accesslevel_' + irAccesslevel) }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="grid auto-cols-fr grid-flow-col gap-6 tablet:flex tablet:flex-wrap">
                        <div v-if="adminPermissions[Feature.DATA_MARKETING]" class="grow">
                            <label>{{ translate('datamarketing') }}</label>
                            <select v-model="formUser.permissions.dataMarketingAccessLevel">
                                <option v-for="dmAccesslevel in accountStore.dmAccesslevels" :value="dmAccesslevel"
                                        :key="'dm'+dmAccesslevel">
                                    {{ translate('internationaal_accesslevel_' + dmAccesslevel) }}
                                </option>
                            </select>
                        </div>
                        <div v-if="adminPermissions[Feature.RADAR]" class="grow">
                            <label>Radar</label>
                            <select v-model="formUser.permissions.radarAccessLevel">
                                <option v-for="radarAccessLevel in accountStore.radarAccesslevels" :value="radarAccessLevel"
                                        :key="'radar'+radarAccessLevel">
                                    {{ translate('internationaal_accesslevel_' + radarAccessLevel) }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>

                <div class="mt-10" v-if="formUser.mobileInstalls.length > 0">
                    <table class="table-medium">
                        <thead>
                        <tr>
                            <th>{{ translate('mobile_devices') }}</th>
                            <th>{{ translate('status') }}</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(device) in sortedMobileInstalls" role="row" class="group">
                            <td>{{ getMobileDeviceName(device) }}</td>
                            <td>
                                <span class="badge" :class="getMobileDeviceClass(device)">{{translate('tab_label_'+getMobileDeviceStatus(device))}}</span>
                            </td>
                            <td class="text-right">
                                <button
                                    :class="{'btn btn-sm btn-danger invisible group-hover:visible hover:bg-red-400': device.isActive,'btn btn-sm btn-success-hover invisible group-hover:visible hover:bg-pine-400 hover:text-white': !device.isActive}"
                                    v-on:click.prevent.stop="changeDeviceStatus(device.id, !device.isActive)"
                                    :title="device.isActive ? translate('deactivate_device') : translate('activate_device')">
                                    <i v-if="device.isActive" class="far fa-ban"></i>
                                    <i v-else class="far fa-check"></i>
                                </button>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex justify-between mt-6">
                    <button type="button" class="btn" @click.stop.prevent="close">{{ translate("btn_cancel") }}</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-save mr-1.5"></i> {{ translate("btn_save") }}</button>
                </div>
            </form>
        </template>
    </cw-modal>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {useAccountStore} from "../store";
import {MobileInstalls, User} from "./types";
import {Feature} from "../../../types/dto/Feature";
import Ajax from "../../../lib/Ajax";
import Confirm from "../../../lib/Confirmation";
import Toast from "../../../lib/Toast";

export default defineComponent({
    name: "edit-user-modal",
    props: {
        user: {
            type: Object as () => User,
            required: true
        }
    },
    data(){
        return {
            accountStore: useAccountStore(),
            formUser: this.createFormUser(this.user)
        }
    },
    watch: {
        'user.userId'() {
            this.formUser = this.createFormUser(this.user);
        }
    },
    computed: {
        Feature() {
            return Feature
        },
        isLoggedInAsAdmin(): boolean {
            return this.accountStore.isLoggedInAsAdmin;
        },
        cannotDemoteFromAdmin(): boolean {
            return this.totalAmountOfAdmins === 1 && (this.user.isAdmin ?? false)
        },
        totalAmountOfAdmins(): number {
            return this.accountStore.users.filter(u => u.isAdmin).length;
        },
        adminPermissions(): Record<Feature, boolean> {
            return this.store.permissions;
        },
        sortedMobileInstalls(): MobileInstalls[] {
            return (this.formUser.mobileInstalls ?? []).orderBy(c => (c.isActive ? "0-" : "1-") + c.name.toLowerCase(), "asc");
        }
    },
    methods: {
        createFormUser(user: User): User {
            return {
                ...user,
                permissions: {
                    ...user.permissions
                },
                mobileInstalls: user.mobileInstalls?.map(device => ({
                    ...device
                })) ?? []
            };
        },
        getMobileDeviceName(device: MobileInstalls): string {
            return device.name.trim() !== '' ? device.name : "Unknown";
        },
        getMobileDeviceClass(device: MobileInstalls): string {
            return device.isActive ? "success" : "danger";
        },
        getMobileDeviceStatus(device: MobileInstalls): string {
            return device.isActive ? "active" : "deactivated";
        },
        async changeDeviceStatus(deviceId: string, active: boolean): Promise<void> {
            try {
                await Ajax.postAsync(
                '/ajax/account/change-device-status',
                {
                    userId: this.formUser.userId,
                    installId: deviceId,
                    isActive: active
                });

                const device = this.formUser.mobileInstalls.find(x => x.id === deviceId);
                if (device)
                    device.isActive = active;
                
            } catch (error: unknown) {
                Toast.error(error as string);
            }
        },
        async save(formUser: User) {
            await this.accountStore.savePermissions(formUser);
            this.user.isAdmin = formUser.isAdmin;
            this.user.email = formUser.email;
            this.user.name = formUser.name;
            
            this.close();
        },
        close() {
            this.store.closeModal();
        }
    }
})
</script>